import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import * as PropTypes from "prop-types";
import classnames from "classnames";

import Backdrop from "../Backdrop/Backdrop";
import Scroll from "../Scroll/Scroll";
import Portal from "../Portal/Portal";

import useFocus from "../../hooks/useFocus";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";

import { _experimental_isNextApp } from "../../utils/assembly";
import { DATA_ATTR_HIDE_POPUP_ON_CLICK, PopupPositionPattern } from "./popup.constants";
import {
  getBoundaryRectConsideringStackingContext, getEventCoordinates,
  isBrowserSupportsSmoothScroll, parseDOMRectToObject,
} from "../../utils/dom";
import { EL_ID_PORTAL_MAIN_AFTER } from "../Portal/PortalMainAfter/constants";
import { debounce } from "../../utils/optimization";
import { handleKeyDownExitFloatWindow } from "../../utils/keyboard-navigation/keyboard-navigation";
import { isNumberBetweenEquals } from "@/shared/utils/number/number";
import useRegulateGlobalClassNameIsAnyPopupLikeWindowOpened
  from "@/shared/hooks/useRegulateGlobalClassNameIsAnyPopupLikeWindowOpened";
import whatInput from "@/shared/what-input/source";
import { INPUT } from "@/shared/what-input/constants";

import styles from "./popup.module.scss";

const POINTER_SIZE = 14;

// e.g. Book Landing Site, Available Languages Popup on desktop zoom 150%+.
const DELTA_MIN_TO_ALLOW_APPLY_SIDE_FROM_PROPS = 100;

const Popup = forwardRef(({
  isShown,

  isUseLockBodyScroll = _experimental_isNextApp,

  // Crutches for ML first time open window
  isRegulateMaxSize = true,
  isCorrectPosition = true,
  // ======================================

  isShowPointer,

  preventCoverHeader = true,
  hasConstantSizes,
  isContentDynamic,
  usePositionAbsolute,
  refElementScrollIedIntoViewBeforeBecameShown,

  focusOnBecameVisible = true,

  hasBackdrop = true,
  hasBackdropTransparent,

  hideOnScroll,
  hideOnResize = true,
  hideOnMouseLeave,
  allowMoveMouseOnTooltip = true,
  hideOnBlur,

  id,
  className,
  renderViewClassName,
  rootProps,
  contentProps,
  backdropProps,

  anchorEl,
  anchorRect,
  boundaryRect,

  maxHeight,
  maxWidth,
  beautyGapHorizontal = 16,
  beautyGapVertical = 16,

  xDesired,
  yDesired,
  
  leftSide: leftSideFromProps,
  onTop: onTopFromProps,
  
  positionPattern,

  onHide,

  children,
}, ref) => {
  useLockBodyScroll(!!(isShown && isUseLockBodyScroll));
  useRegulateGlobalClassNameIsAnyPopupLikeWindowOpened(!!isShown);

  const [elContent, setElContent] = useState(null);

  const [isShownAtTop, setIsShownAtTop] = useState(false);
  const [isShownAtLeft, setIsShownAtLeft] = useState(false);
  const [styleRoot, setStyleRoot] = useState(null);
  const [styleScrollWrap, setStyleScrollWrap] = useState(null);
  const [styleScrollView, setStyleScrollView] = useState(null);

  const [triggerComputePosition, setTriggerComputePosition] = useState();
  const [triggerCorrectPosition, setTriggerCorrectPosition] = useState();
  const [triggerReDefineConstantSizes, setTriggerReDefineConstantSizes] = useState();

  const [sizeAfterInitiatedAndCorrected, setSizeAfterInitiatedAndCorrected] = useState(null);

  const [isInitPositionAndSizesCorrected, setIsInitPositionAndSizesCorrected] = useState(false);
  const refIsScrolledToPropsElementAfterBecameShown = useRef(false);
  const refIsInitPositionAndSizesCorrected = useRef(isInitPositionAndSizesCorrected);
  refIsInitPositionAndSizesCorrected.current = isInitPositionAndSizesCorrected;

  let classNameRootProps;
  let styleRootProps;
  let propsRestRoot;
  if (rootProps) {
    for (const key in rootProps) {
      if (key === "className") {
        classNameRootProps = rootProps[key];
      } else if (key === "style") {
        styleRootProps = rootProps[key];
      } else {
        if (!propsRestRoot) {
          propsRestRoot = {};
        }

        propsRestRoot[key] = rootProps[key];
      }
    }
  }

  let classNameContentProps;
  let styleContentProps;
  let scrollContentProps = {};
  let propsRestContent;
  if (contentProps) {
    for (const key in contentProps) {
      if (key === "className") {
        classNameContentProps = contentProps[key];
      } else if (key === "style") {
        styleContentProps = contentProps[key];
      } else if (key === "renderView" || key === "ref") {
        scrollContentProps[key] = contentProps[key]
      }else {
        if (!propsRestContent) {
          propsRestContent = {};
        }

        propsRestContent[key] = contentProps[key];
      }
    }
  }

  let classNameBackdrop;
  let styleBackdrop;
  let restPropsBackdrop;
  if (backdropProps) {
    for (const key in backdropProps) {
      if (key === "className") {
        classNameBackdrop = backdropProps[key];
      } else if (key === "style") {
        styleBackdrop = backdropProps[key];
      } else {
        if (!restPropsBackdrop) {
          restPropsBackdrop = {};
        }

        restPropsBackdrop[key] = backdropProps[key];
      }
    }
  }

  const getBoundaryRect = useCallback(() => {
    let rect;

    if (boundaryRect) {
      rect = boundaryRect;
    } else if (typeof window === "undefined") {
      rect = {};
    } else {
      let top;

      if (preventCoverHeader) {
        const elHeader = document.querySelector("header");

        if (elHeader) {
          top = elHeader.offsetHeight;
        }
      }

      if (top === undefined) {
        top = beautyGapVertical;
      }

      const left = beautyGapHorizontal;
      const right = window.innerWidth - beautyGapHorizontal;
      const bottom = window.innerHeight - beautyGapVertical;

      rect = {
        top,
        left,
        right,
        bottom,
        width: right - left,
        height: bottom - top,
      };
    }

    return rect;
  }, [preventCoverHeader, boundaryRect, beautyGapVertical, beautyGapHorizontal]);

  const getAnchorRect = useCallback(() => {
    let rect;

    if (anchorRect) {
      rect = {
        x: anchorRect.x ?? anchorRect.left ?? anchorRect.right,
        y: anchorRect.y ?? anchorRect.top ?? anchorRect.bottom,
        left: anchorRect.left ?? anchorRect.x ?? anchorRect.right,
        right: anchorRect.right ?? anchorRect.x ?? anchorRect.left,
        top: anchorRect.top ?? anchorRect.y ?? anchorRect.bottom,
        bottom: anchorRect.bottom ?? anchorRect.y ?? anchorRect.top,
        width: anchorRect.width ?? 0,
        height: anchorRect.height ?? 0,
      };
    } else {
      rect = getBoundaryRectConsideringStackingContext(anchorEl);
    }

    if (isShowPointer && rect) {
      rect = parseDOMRectToObject(rect);

      rect.x -= POINTER_SIZE / 2;
      rect.y -= POINTER_SIZE / 2;
      rect.width += POINTER_SIZE;
      rect.height += POINTER_SIZE;
      rect.top -= POINTER_SIZE / 2;
      rect.left -= POINTER_SIZE / 2;
      rect.bottom += POINTER_SIZE / 2;
      rect.right += POINTER_SIZE / 2;
    }

    return rect;
  }, [anchorRect, anchorEl, isShowPointer]);

  const hide = useCallback((e) => {
    onHide(e);

    if (e) {
      e.stopPropagation();
    }
  }, [onHide]);

  const handleKeyDownContent = (e) => {
    if (isShown) {
      handleKeyDownExitFloatWindow(e, hide);
    }
  };

  const handleClickRoot = (e) => {
    if (
      e.target.classList.contains(DATA_ATTR_HIDE_POPUP_ON_CLICK)
      || e.target.closest("[" + DATA_ATTR_HIDE_POPUP_ON_CLICK + "]")
    ) {
      hide(e);
    }
  };

  const handleBlurRoot = (e) => {
    if (hideOnBlur && isShown) {
      const root = e.currentTarget
      if (!root) {
        return;
      }

      // Not investigated:
      // – without a {setTimeout}, document.activeElement equals to "body" element always.
      setTimeout(() => {
        if (
          root !== document.activeElement
          && !root.contains(document.activeElement)
          && whatInput.ask() === INPUT.keyboard

          // Prevent closing, if some float-window (e.g. modal or another popup)
          // was opened from this popup.
          && !document.getElementById(EL_ID_PORTAL_MAIN_AFTER)?.contains(document.activeElement)
        ) {
          hide(e);
        }
      });
    }
  };

  const initiatePositionAndMaxSizes = useCallback(() => {
    const anchorRect = getAnchorRect();

    if (anchorRect) {
      const boundaryRect = getBoundaryRect();

      const deltaTop = Math.min(anchorRect.top - boundaryRect.top, anchorRect.top);
      const deltaLeft = Math.min(
        anchorRect.right - boundaryRect.left, anchorRect.right
      );
      const deltaRight = Math.min(
        boundaryRect.right - anchorRect.left,
        window.innerWidth - anchorRect.left,
      );
      const deltaBottom = Math.min(
        boundaryRect.bottom - anchorRect.bottom,
        window.innerHeight - anchorRect.bottom,
      );

      // ===== Position regarding Anchor and Boundary Element/Rect ============
      let atTop = onTopFromProps ?? deltaTop > deltaBottom;
      let atLeft = leftSideFromProps ?? deltaLeft > deltaRight;

      if (positionPattern === PopupPositionPattern.AT_RIGHT_ANCHOR_SIDE) {
        atLeft = false;
      }

      if (
        onTopFromProps
        && deltaTop < DELTA_MIN_TO_ALLOW_APPLY_SIDE_FROM_PROPS
        && deltaBottom > deltaTop
      ) {
        atTop = false
      }
      if (
        leftSideFromProps
        && deltaLeft < DELTA_MIN_TO_ALLOW_APPLY_SIDE_FROM_PROPS
        && deltaRight > deltaLeft
      ) {
        atLeft = false;
      }
      // ========================================================

      // ===================== Sizes ============================
      const maxWidthForCurrentPosition = atLeft ? deltaLeft : deltaRight;
      const maxHeightForCurrentPosition = atTop ? deltaTop : deltaBottom;

      const maxWidthFinal = Math.min(
        ...[
          isContentDynamic ? maxWidthForCurrentPosition : undefined,
          boundaryRect.width,
          maxWidth,
          styleContentProps?.maxWidth,
          styleRoot?.maxWidth,
        ].filter((n) => {
          return typeof n === "number" && n !== 0 && !isNaN(n) && n !== Infinity;
        })
      );
      const maxHeightFinal = Math.min(
        ...[
          maxHeightForCurrentPosition,
          boundaryRect.height,
          typeof maxHeight === "function" ? maxHeight() : maxHeight,
          styleContentProps?.maxHeight,
          styleRoot?.maxHeight,
        ].filter((n) => {
          return typeof n === "number" && n !== 0 && !isNaN(n) && n !== Infinity;
        })
      );

      const styleSizes = isRegulateMaxSize ? {
        maxWidth: maxWidthFinal,
        maxHeight: maxHeightFinal,
      } : undefined;

      // Prevent min size from props exceed max size X.
      if (styleContentProps?.minWidth) {
        styleSizes.minWidth = Math.min(styleContentProps?.minWidth, maxWidthFinal);
      }
      // Prevent min size from props exceed max size Y.
      if (styleContentProps?.minHeight) {
        styleSizes.minHeight = Math.min(styleContentProps?.minHeight, maxHeightFinal);
      }

      setStyleScrollView(styleSizes);
      // ========================================================

      // ===================== Position ============================
      if (usePositionAbsolute) {
        const styleRoot = {
          position: "absolute",
          zIndex: 100,
        };
        let strStyleTransform = "";

        if (atTop) {
          styleRoot.top = 0 + "px";
          strStyleTransform += "translateY(-100%)";
        } else {
          styleRoot.bottom = 0 + "px";
          strStyleTransform += "translateY(100%)";
        }

        if (atLeft) {
          styleRoot.right = 0 + "px";
        } else {
          styleRoot.left = 0 + "px";
        }

        styleRoot.transform = strStyleTransform;

        setStyleScrollWrap({ ...styleContentProps, ...styleSizes, });
        setStyleRoot({ ...styleRoot, ...styleRootProps });
      } else {
        const styleScrollWrapPosition = {
          position: "fixed",
          width: "unset",
          height: "unset",
        };

        if (positionPattern === PopupPositionPattern.AT_RIGHT_ANCHOR_SIDE) {
          if (atLeft) {
            styleScrollWrapPosition.right = anchorRect.right + "px";
          } else {
            styleScrollWrapPosition.left = anchorRect.right + "px";
          }

          if (atTop) {
            styleScrollWrapPosition.bottom = Math.max(0, window.innerHeight - anchorRect.bottom) + "px";
          } else {
            styleScrollWrapPosition.top = Math.max(boundaryRect.top, anchorRect.top) + "px";
          }
        } else {
          if (yDesired) {
            styleScrollWrapPosition.top = yDesired + "px";
          } else {
            if (atTop) {
              styleScrollWrapPosition.bottom = Math.max(0, window.innerHeight - anchorRect.top) + "px";
            } else {
              styleScrollWrapPosition.top = Math.max(boundaryRect.top, anchorRect.bottom) + "px";
            }
          }

          if (xDesired) {
            styleScrollWrapPosition.left = xDesired + "px";
          } else {
            if (atLeft) {
              styleScrollWrapPosition.right = Math.max(0, window.innerWidth - anchorRect.right) + "px";
            } else {
              styleScrollWrapPosition.left = anchorRect.left + "px";
            }
          }
        }

        setStyleScrollWrap({ ...styleScrollWrapPosition, ...styleContentProps, ...styleSizes, });
        setStyleRoot(styleRootProps);
      }
      // ========================================================

      setIsShownAtTop(atTop);
      setIsShownAtLeft(atLeft);
      setTriggerCorrectPosition(Math.random());
    }
  }, [
    xDesired,
    leftSideFromProps,
    onTopFromProps,
    usePositionAbsolute,
    isRegulateMaxSize,
    styleContentProps, maxWidth, maxHeight, isContentDynamic,
    getAnchorRect, getBoundaryRect,
    positionPattern,
    styleRootProps,
    styleRoot?.maxWidth,
    styleRoot?.maxHeight,
  ]);

  const cleanUpPositionAndSizes = () => {
    setStyleRoot(null);
    setStyleScrollWrap(null);
    setStyleScrollView(null);
    setSizeAfterInitiatedAndCorrected(null);
    setIsInitPositionAndSizesCorrected(false);
    refIsScrolledToPropsElementAfterBecameShown.current = false;
  };

  // STEP 1:
  // - scroll to {refElementScrollIedIntoViewBeforeBecameShown}
  // - initiates position and max sizes.
  useEffect(() => {
    if (isShown) {
      const onScrollToPropsElementDone = () => {
        if (usePositionAbsolute) {
          // Necessarily make anchor element visible in the view,
          // because popup's position is "absolute" – means popup is always near the button.
          const boundaryRect = getBoundaryRect();
          const anchorRect = getAnchorRect();

          let isScrollPerformed = false;

          if (anchorRect.top > boundaryRect.bottom) {
            isScrollPerformed = true;
            anchorEl.scrollIntoView(false);
          } else if (anchorRect.bottom < boundaryRect.top) {
            isScrollPerformed = true;
            anchorEl.scrollIntoView();
          }
          // ================================================================================

          if (isScrollPerformed) {
            setTimeout(initiatePositionAndMaxSizes, isBrowserSupportsSmoothScroll ? 500 : 0);
          } else {
            initiatePositionAndMaxSizes();
          }
        } else {
          initiatePositionAndMaxSizes();
        }
      };

      if (
        refElementScrollIedIntoViewBeforeBecameShown?.current
        && !refIsScrolledToPropsElementAfterBecameShown.current
      ) {
        refElementScrollIedIntoViewBeforeBecameShown.current.scrollIntoView(false);

        refIsScrolledToPropsElementAfterBecameShown.current = true;

        setTimeout(onScrollToPropsElementDone, isBrowserSupportsSmoothScroll ? 500 : 0);
      } else {
        onScrollToPropsElementDone();
      }
    }
  }, [getAnchorRect, isShown, triggerComputePosition]);

  // STEP 2 - Corrects position and max sizes after first rendered in DOM.
  const isStyleScrollWrapSet = !!styleScrollWrap;
  useEffect(() => {
    if (isShown && isStyleScrollWrapSet) {
      if (isCorrectPosition) {
        if (elContent) {
          const rect = elContent.getBoundingClientRect();
          const boundaryRect = getBoundaryRect();
          const anchorRect = getAnchorRect();

          if (!anchorRect) {
            return;
          }

          // Ignore delta less than 2px.
          // Delta is near 2px on Samsung Galaxy M52 v11.0 for 3-dots in Reader,
          // and the correction in such case breaks the popup size if it uses "Css grid".
          const isLeftOverBoundary = boundaryRect.left - rect.left > 2;
          const isRightOverBoundary = rect.right - boundaryRect.right > 2;

          if (isLeftOverBoundary || isRightOverBoundary) {
            if (usePositionAbsolute) {
              const maxWidth = isLeftOverBoundary
                ? boundaryRect.right - anchorRect.left
                : anchorRect.right - boundaryRect.left;

              setStyleScrollWrap((v) => ({
                ...v,
                maxWidth,
              }));
              setStyleRoot((v) => ({
                ...v,
                left: isLeftOverBoundary ? 0 : "unset",
                right: isLeftOverBoundary ? "unset" : 0,
                maxWidth,
              }));

              setIsShownAtLeft(true);
            } else {
              const maxWidth = boundaryRect.width;

              setStyleScrollWrap((v) => ({
                ...v,
                left: isLeftOverBoundary ? boundaryRect.left : "unset",
                right: isLeftOverBoundary
                  ? "unset"
                  : window.innerWidth - boundaryRect.right,
                maxWidth,
              }));
              setStyleRoot((v) => ({
                ...v,
                maxWidth,
              }));

              setIsShownAtLeft(true);
            }
          }

          setSizeAfterInitiatedAndCorrected(null);
          setTriggerReDefineConstantSizes(Math.random());
          setIsInitPositionAndSizesCorrected(true);
        }
      } else {
        setIsInitPositionAndSizesCorrected(true);
      }
    }
  }, [
    beautyGapHorizontal, usePositionAbsolute,
    isCorrectPosition, isShown, isStyleScrollWrapSet,
    elContent, triggerCorrectPosition,
    getBoundaryRect, getAnchorRect,
  ]);

  // STEP 3 - Stores sizes, if {hasConstantSizes}.
  useEffect(() => {
    if (hasConstantSizes && isInitPositionAndSizesCorrected && elContent) {
      setSizeAfterInitiatedAndCorrected({
        width: elContent.offsetWidth,
        height: elContent.offsetHeight,
      });
    }
  }, [hasConstantSizes, isInitPositionAndSizesCorrected, triggerReDefineConstantSizes]);

  // Cleans up state after became hidden.
  useEffect(() => {
    return () => {
      if (isShown) {
        cleanUpPositionAndSizes();
      }
    };
  }, [isShown]);

  // Smart listening for a popup content and anchor element mouse leave.
  useEffect(() => {
    let listener, timerId;

    const appropriate = hideOnMouseLeave && isShown && elContent && isInitPositionAndSizesCorrected;

    if (appropriate) {
      const anchorRect = getAnchorRect();

      listener = (e) => {
        const { x, y } = getEventCoordinates(e);

        const contentRect = elContent.getBoundingClientRect();
        const contentRectExtended = parseDOMRectToObject(contentRect);

        if (isShownAtTop) {
          contentRectExtended.bottom += anchorRect.height;
        } else {
          contentRectExtended.top -= anchorRect.height;
          contentRectExtended.y -= anchorRect.height;
        }

        contentRectExtended.height += anchorRect.height

        const outOfPopupRectExtended = !isNumberBetweenEquals(x, contentRectExtended.left, contentRectExtended.right)
          || !isNumberBetweenEquals(y, contentRectExtended.top, contentRectExtended.bottom);
        const outOfAnchorRect = !isNumberBetweenEquals(x, anchorRect.left, anchorRect.right)
          || !isNumberBetweenEquals(y, anchorRect.top, anchorRect.bottom);

        if (outOfPopupRectExtended) {
          hide(e);
        } else {
          clearTimeout(timerId);

          if (outOfAnchorRect) {
            if (allowMoveMouseOnTooltip) {
              const outOfPopupRect = !isNumberBetweenEquals(x, contentRect.left, contentRect.right)
                || !isNumberBetweenEquals(y, contentRect.top, contentRect.bottom);

              if (outOfPopupRect) {
                timerId = setTimeout(hide, 400);
              }
            } else {
              hide(e);
            }
          }
        }
      };

      document.addEventListener("mousemove", listener);
    }

    return () => {
      if (appropriate) {
        document.removeEventListener("mousemove", listener);
        clearTimeout(timerId);
      }
    };
  }, [
    isShown, isInitPositionAndSizesCorrected, allowMoveMouseOnTooltip, hideOnMouseLeave,
    elContent, isShownAtTop, getAnchorRect
  ]);

  // Updates position on window resize.
  useEffect(() => {
    let listener;

    if (isShown) {
      let widthPrevRecalc = window.innerWidth;
      let heightPrevRecalc = window.innerHeight;

      const triggerRecalcPosition = debounce(() => {
        if (widthPrevRecalc !== window.innerWidth || heightPrevRecalc !== window.innerHeight) {

          setTriggerComputePosition(Math.random());

          widthPrevRecalc = window.innerWidth;
          heightPrevRecalc = window.innerHeight;
        }
      }, 125);

      listener = () => {
        triggerRecalcPosition();
      };

      window.addEventListener("resize", listener);
    }

    return () => {
      if (isShown) {
        window.removeEventListener("resize", listener);
      }
    };
  }, [isShown]);

  const isRenderContent = !!(isShown && isStyleScrollWrapSet);

  useFocus(focusOnBecameVisible && isRenderContent && elContent, {
    current: elContent,
  }, {
    isScrollFocusedElementIntoView: !refElementScrollIedIntoViewBeforeBecameShown?.current,
  });

  // Fast-fix. Close fixed positioned popups of Main React Application,
  // if scrolled the layer of content + footer.
  useEffect(() => {
    let mainScroller;
    if (!_experimental_isNextApp) {
      mainScroller = document.querySelector(".main-application");
      mainScroller?.children?.[0]?.addEventListener("scroll", hide);
    }

    return () => {
      mainScroller?.children?.[0]?.removeEventListener("scroll", hide);
    };
  }, [hide]);

  if (!isRenderContent) {
    return null;
  }

  const styleConstantSizes = sizeAfterInitiatedAndCorrected
    ? sizeAfterInitiatedAndCorrected : undefined;

  let pointerClass;

  if (isShowPointer) {
    pointerClass = isShownAtTop
      ? isShownAtLeft ? styles["right-bottom"] : styles["left-bottom"]
      : isShownAtLeft ? styles["right-top"] : styles["left-top"];
  }

  const jsxContent = (
    <div
      ref={ref}
      id={id}
      onBlur={handleBlurRoot}
      className={classnames(styles["root"], className, classNameRootProps, {
        [styles["ready-to-show"]]: isInitPositionAndSizesCorrected,
      })}
      {...propsRestRoot}
      style={styleRoot}
    >
      <div
        tabIndex={1}
        ref={setElContent}
        className={classnames(styles["content"], classNameContentProps, {
          [styles["is-shown-at-top"]]: isShownAtTop,
          [styles["is-shown-at-left"]]: isShownAtLeft,
        })}
        style={{
          ...styleScrollWrap, ...styleConstantSizes,
        }}
        onKeyDown={handleKeyDownContent}
        onClick={handleClickRoot}
        {...propsRestContent}
      >
        {pointerClass && <div className={classnames(styles["pointer"], pointerClass)} />}
        <Scroll
          renderViewClassName={renderViewClassName}
          renderViewStyle={styleScrollView}

          autoHide={false} // always show scrollbar, to understand if there is more content in popup
          universal
          hasStaticPosition
          noHidePopups
          className={styles["scroll"]}
          {...scrollContentProps}
        >
          {typeof children === "function" ? children({ hide }) : children}
        </Scroll>
      </div>

      {hasBackdrop && (
        <Backdrop
          className={classnames(styles["backdrop"], classNameBackdrop)}
          onClick={hide}
          style={hasBackdropTransparent ? {
            backgroundColor: "transparent",
            ...styleBackdrop,
          } : styleBackdrop}
          {...restPropsBackdrop}
        />
      )}
    </div>
  );

  if (usePositionAbsolute) {
    if (!styleRoot) {
      return null;
    }

    return jsxContent;
  }

  return (
    <Portal id={EL_ID_PORTAL_MAIN_AFTER}>
      {jsxContent}
    </Portal>
  );
});

Popup.propTypes = {
  className: PropTypes.string,
  isShown: PropTypes.bool,
  hideOnMouseLeave: PropTypes.bool,
  hideOnScroll: PropTypes.bool,
  contentProps: PropTypes.object,
  backdropProps: PropTypes.object,
  anchorEl: PropTypes.object,
  boundaryRect: PropTypes.object,
  onHide: PropTypes.func.isRequired,
  hasBackdrop: PropTypes.bool,
  hasBackdropTransparent: PropTypes.bool,
  children: PropTypes.any,
  positionPattern: PropTypes.oneOf(Object.values(PopupPositionPattern)),
};

export default Popup;
